import type {
  FunLogger,
  GetIdentityAssertion,
  RouteIdentity,
  RouteLimits,
} from '@funkit/connect-core'
import {
  ROUTE_LIMITS_SCOPE,
  createIdentityAssertionProvider,
  recordRouteLimits,
  routeLimitsKey,
  sendAuthedFiatTransition,
  useEffectEvent,
} from '@funkit/connect-core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'

/** The two fields of a discovered payment method this screen reads. */
interface PaymentMethodOption {
  category?: string
  /** Optional: the backend answering this build may predate the field. */
  maxQuoteableAmount?: string | null
}

interface PaymentMethodsResponse {
  paymentMethods?: PaymentMethodOption[]
}

/** Under the route-limits scope, so a step-up that raises the tier clears this too. */
const QUOTEABLE_CEILING_SCOPE = [...ROUTE_LIMITS_SCOPE, 'quoteable'] as const

/**
 * The ceiling `paymentMethod` names, in the requested currency — the response
 * carries none of its own. Another category's ceiling is not this buyer's, and
 * anything absent, null or unreadable is no ceiling at all.
 */
export function quoteableCeiling(
  response: PaymentMethodsResponse | undefined,
  paymentMethod: string,
  currency: string,
): RouteLimits | undefined {
  const option = response?.paymentMethods?.find(
    (candidate) => candidate.category === paymentMethod,
  )
  const max = option?.maxQuoteableAmount
  if (max === undefined || max === null) {
    return undefined
  }
  const value = Number(max)
  if (!Number.isFinite(value) || value <= 0) {
    return undefined
  }
  return { max, currency }
}

/**
 * What the buyer may still spend on this route, learned without asking for a
 * price. Absent unless the answer is certain: a ceiling too high costs a round
 * trip, one too low refuses a purchase the server would have served.
 */
export function useQuoteableCeiling({
  route,
  logger,
  getIdentityAssertion,
}: {
  route: RouteIdentity
  logger: FunLogger
  getIdentityAssertion?: GetIdentityAssertion
}): RouteLimits | undefined {
  const queryClient = useQueryClient()
  const { currency, paymentMethod, userId } = route
  const country = route.geo?.alpha2

  // Discovery mints on its own rather than through the deposit flow's provider,
  // whose minter reports a deposit failure — and the buyer has asked for
  // nothing yet. `useEffectEvent` so an inlined host callback keeps one cache.
  const mint = useEffectEvent(() =>
    getIdentityAssertion === undefined
      ? Promise.reject(new Error('getIdentityAssertion is not configured'))
      : getIdentityAssertion(),
  )
  const canMint = getIdentityAssertion !== undefined
  const identityAssertionProvider = useMemo(
    // The subject bounds the cache: a host swapping users starts a new one.
    () => (canMint ? createIdentityAssertionProvider(mint) : undefined),
    [canMint, userId, mint],
  )

  // The endpoint takes no routing overrides, so a pinned provider set cannot
  // change this answer and does not key it.
  const queryKey = [
    ...QUOTEABLE_CEILING_SCOPE,
    route.apiKey,
    userId,
    currency ?? '',
    paymentMethod,
    route.cryptoCurrencyCode,
    route.chainId,
    country ?? '',
  ] as const

  // Gated like a quote: the request names the currency and the country, and
  // both are resolved by lookups that land after the screen does.
  const canFetch =
    currency !== undefined && country !== undefined && country !== ''

  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      if (currency === undefined || country === undefined) {
        throw new Error('Payment methods request has no currency or country')
      }
      const search = (
        [
          ['fiatCurrency', currency],
          ['country', country],
          ['chainId', route.chainId],
          ['cryptoCurrencyCode', route.cryptoCurrencyCode],
        ] as const
      )
        .map(([name, value]) => `${name}=${encodeURIComponent(value)}`)
        .join('&')
      try {
        const response = await sendAuthedFiatTransition<PaymentMethodsResponse>(
          {
            apiKey: route.apiKey,
            logger,
            signal,
            params: { endpoint: `GET /fiat/payment-methods?${search}` },
          },
          identityAssertionProvider,
        )
        const learned = quoteableCeiling(response, paymentMethod, currency)
        // Logged on both paths, so a session that refused an amount without
        // sending a request still says where the ceiling came from.
        logger.info('connect-rn:fiat:quoteable_ceiling', {
          paymentMethod,
          currency,
          max: learned?.max ?? null,
        })
        // `null` rather than `undefined`: a query may not resolve to undefined,
        // and "asked, and there is no ceiling" is an answer worth caching.
        return learned ?? null
      } catch (caught) {
        // A rejected identity and a backend without the field are the same
        // silence otherwise, and only one of them is a broken rollout.
        if (!signal.aborted) {
          logger.warn('connect-rn:fiat:payment_methods_failed', {
            paymentMethod,
            cause: caught instanceof Error ? caught.message : String(caught),
          })
        }
        throw caught
      }
    },
    enabled: canFetch,
    retry: false,
    // A ceiling belongs to the route, not to a moment, so one answer serves
    // the whole session. What raises it clears the scope instead.
    staleTime: Infinity,
  })

  const ceiling = query.data ?? undefined

  // Recorded where a refusal's maximum is recorded, so the two are one answer
  // to "what is the most I can buy" and the narrower of them wins.
  useEffect(() => {
    if (ceiling === undefined) {
      return
    }
    recordRouteLimits(queryClient, route, ceiling)
    // `route` is rebuilt every render; its own fields are the identity.
  }, [queryClient, ceiling?.max, ceiling?.currency, ...routeLimitsKey(route)])

  return ceiling
}
